/**
 * lib/parentLinkRequests.ts
 * ------------------------------------------------------------------
 * Self-service parent onboarding (parent-initiated). Pure types +
 * matching helpers shared by the admin review screen.
 *
 * The existing route into the parent portal is admin-initiated (mint a
 * key, email it, parent redeems). This is the INVERSE: a parent arrives
 * from the school website, fills the public request form
 * (public/parent-link-request.html) with their details + each child's
 * name, class and date of birth, and the office matches each claimed
 * child to a real pupil and approves — reusing createParentAccount.
 *
 * DOB + class are the verification signals; nothing is auto-selected.
 * ------------------------------------------------------------------ */

export const PARENT_LINK_REQUESTS = 'reportParentLinkRequests';

export type LinkRequestStatus = 'pending' | 'approved' | 'rejected' | 'invalid' | 'duplicate';

export interface ClaimedChild {
  firstName: string;
  surname: string;
  /** Class id chosen from the dropdown (strong match key), if resolved. */
  classId?: string | null;
  /** Class display name as the parent selected it. */
  className: string;
  /** ISO date (YYYY-MM-DD) — the verification signal. */
  dateOfBirth: string;
  /** Set by the admin during review. */
  matchedStudentId?: string;
}

export interface ParentLinkRequest {
  id: string;
  status: LinkRequestStatus;
  source?: string;
  parentName: string;
  parentEmail: string;
  parentPhone: string;
  /** Required on the public form since Aug 2026. Absent on requests taken
   *  before that, so it stays optional on the type. */
  parentOccupation?: string;
  relationship?: string;
  note?: string | null;
  children: ClaimedChild[];
  createdAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewedByName?: string;
  decisionNote?: string;
  linkedUid?: string;
}

/** A candidate pupil the admin can match a claimed child to. */
export interface Candidate {
  id: string;
  fullName: string;
  className: string;
  classId?: string;
  dateOfBirth?: string;
}

const norm = (s?: string) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

export type DobResult = 'match' | 'mismatch' | 'unknown';

/** Compare the claimed DOB with a pupil's DOB on file. */
export function dobAgreement(claimed?: string, candidateDob?: string): DobResult {
  if (!candidateDob || !claimed) return 'unknown';
  return claimed.slice(0, 10) === candidateDob.slice(0, 10) ? 'match' : 'mismatch';
}

/**
 * Candidate matches for a claimed child on a "2 out of 3" rule — a pupil
 * is suggested when at least TWO of the three parent-supplied signals agree:
 *   • name  — the first name OR surname appears in the pupil's name
 *             (order-independent, so "Rorisang Odirile" matches the pupil
 *             "Odirile Rorisang Aobakwe"),
 *   • class — the class the parent picked, and
 *   • DOB   — an exact date-of-birth match.
 * So name+class (DOB missing/typo'd), name+DOB (wrong class), or class+DOB
 * (name typo) all surface — but a single lone signal does not. Best matches
 * first: more signals, then exact DOB, then a full-name (both-token) hit.
 */
export function rankMatches(child: ClaimedChild, candidates: Candidate[]): Candidate[] {
  const first = norm(child.firstName);
  const sur = norm(child.surname);
  const full = norm(`${child.firstName} ${child.surname}`);
  const rev = norm(`${child.surname} ${child.firstName}`);
  return candidates
    .map((c) => {
      const nm = norm(c.fullName);
      const bothTokens = (!!first && nm.includes(first)) && (!!sur && nm.includes(sur));
      const nameMatch = nm === full || nm === rev || bothTokens
        || (!!first && nm.includes(first)) || (!!sur && nm.includes(sur));
      const classMatch = (!!child.classId && !!c.classId && c.classId === child.classId)
        || (!!child.className && norm(c.className) === norm(child.className));
      const dobMatch = dobAgreement(child.dateOfBirth, c.dateOfBirth) === 'match';
      const hits = (nameMatch ? 1 : 0) + (classMatch ? 1 : 0) + (dobMatch ? 1 : 0);
      const strongName = nm === full || nm === rev || bothTokens;
      return { c, hits, dobMatch, strongName };
    })
    .filter((x) => x.hits >= 2)
    .sort((a, b) =>
      b.hits - a.hits
      || (b.dobMatch ? 1 : 0) - (a.dobMatch ? 1 : 0)
      || (b.strongName ? 1 : 0) - (a.strongName ? 1 : 0)
      || a.c.fullName.localeCompare(b.c.fullName))
    .map((x) => x.c);
}

/** True once every claimed child has been matched to a pupil. */
export function isFullyMatched(children: ClaimedChild[], matches: Record<number, string>): boolean {
  return children.length > 0 && children.every((_, i) => !!matches[i]);
}

/** Map a self-declared relationship to a Guardian role. */
export function relationshipToRole(rel?: string): 'mother' | 'father' | 'guardian' | 'other' {
  if (rel === 'mother' || rel === 'father' || rel === 'guardian') return rel;
  return 'other';
}
